import { useTranslation } from 'react-i18next';
import { Gender } from '@optiflow/types';
import { CustomerLanguage } from '@optiflow/types';
import type { CustomerFormErrors, CustomerFormValues } from '../form';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';

interface CustomerFormFieldsProps {
  values: CustomerFormValues;
  errors: CustomerFormErrors;
  onChange: <K extends keyof CustomerFormValues>(field: K, value: CustomerFormValues[K]) => void;
  /** Quick create shows only what is needed to register in seconds. */
  variant: 'quick' | 'full';
  onMobileBlur?: () => void;
}

/**
 * The customer field set, shared by quick create and the edit form.
 *
 * One component rather than two forms that drift: the only difference between the journeys is
 * which fields are shown, which is a prop, not a copy.
 */
export function CustomerFormFields({
  values,
  errors,
  onChange,
  variant,
  onMobileBlur,
}: CustomerFormFieldsProps) {
  const { t } = useTranslation();
  const isFull = variant === 'full';

  const genderOptions = [
    { value: '', label: t('customers.fields.notSpecified') },
    ...Object.values(Gender)
      .filter((value) => value !== Gender.UNSPECIFIED)
      .map((value) => ({ value, label: t(`customers.gender.${value.toLowerCase()}`) })),
  ];

  const languageOptions = Object.values(CustomerLanguage).map((value) => ({
    value,
    label: t(`customers.language.${value.toLowerCase()}`),
  }));

  // The birth date cannot be in the future; the browser enforces it before the API has to.
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-4">
      <Input
        label={t('customers.fields.mobile')}
        required
        type="tel"
        inputMode="tel"
        dir="ltr"
        autoComplete="off"
        placeholder="0501234567"
        value={values.mobile}
        error={errors.mobile}
        onChange={(event) => onChange('mobile', event.target.value)}
        onBlur={onMobileBlur}
      />

      <Input
        label={t('customers.fields.fullName')}
        required
        autoComplete="off"
        placeholder={t('customers.fields.fullNamePlaceholder')}
        hint={isFull ? undefined : t('customers.fields.fullNameHint')}
        value={values.fullName}
        error={errors.fullName}
        onChange={(event) => onChange('fullName', event.target.value)}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={t('customers.fields.preferredLanguage')}>
          {({ id }) => (
            <Select
              id={id}
              options={languageOptions}
              value={values.preferredLanguage}
              onChange={(event) => onChange('preferredLanguage', event.target.value)}
            />
          )}
        </FormField>

        <FormField label={t('customers.fields.gender')}>
          {({ id }) => (
            <Select
              id={id}
              options={genderOptions}
              value={values.gender}
              onChange={(event) => onChange('gender', event.target.value)}
            />
          )}
        </FormField>
      </div>

      <Input
        label={t('customers.fields.dateOfBirth')}
        type="date"
        max={today}
        dir="ltr"
        value={values.dateOfBirth}
        error={errors.dateOfBirth}
        onChange={(event) => onChange('dateOfBirth', event.target.value)}
      />

      {isFull ? (
        <>
          <Input
            label={t('customers.fields.alternateMobile')}
            type="tel"
            inputMode="tel"
            dir="ltr"
            value={values.alternateMobile}
            error={errors.alternateMobile}
            onChange={(event) => onChange('alternateMobile', event.target.value)}
          />

          <Input
            label={t('customers.fields.email')}
            type="email"
            dir="ltr"
            autoComplete="off"
            value={values.email}
            error={errors.email}
            onChange={(event) => onChange('email', event.target.value)}
          />

          <FormField label={t('customers.fields.notes')}>
            {({ id }) => (
              <textarea
                id={id}
                rows={3}
                maxLength={2000}
                className="border-line focus:border-brand-500 focus:ring-brand-100 w-full rounded-lg border bg-white px-3 py-2.5 text-sm focus:ring-4 focus:outline-none"
                value={values.notes}
                onChange={(event) => onChange('notes', event.target.value)}
              />
            )}
          </FormField>
        </>
      ) : null}
    </div>
  );
}
